import { BillOfLadingError, TrailerType } from "@deliverr/sortation-client";
import { createSuccessNotification, logError, logStart } from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useMemo, useState } from "react";
import { useAsyncFn, useMount, useUnmount } from "react-use";
import { useSetRecoilState } from "recoil";
import { navbarState } from "sortation/base/navbarState";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { isValidShipwellBolId } from "sortation/utils/stringValidation";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { TextButton } from "../../components/TextButton";
import { TrailerCloseoutModal } from "./modals/TrailerCloseoutModal";

export enum TrailerCloseoutScanState {
  AWAITING_BOL_SCAN = "AWAITING_BOL_SCAN",
  AWAITING_SEAL_SCAN = "AWAITING_SEAL_SCAN",
}

export const useTrailerCloseoutScan = () => {
  // Global State
  const setNavbar = useSetRecoilState(navbarState);

  // Local State
  const [bolId, setBolId] = useState<string>("");
  const [trailerId, setTrailerId] = useState<string>("");
  const [sealNumber, setSealNumber] = useState<string>("");
  const [trailerCloseoutScanState, setTrailerCloseoutScanState] = useState<TrailerCloseoutScanState>(
    TrailerCloseoutScanState.AWAITING_BOL_SCAN
  );

  // Hooks
  const { addAutoCloseNotification, resetNotifications, successResponse, infoResponse, emitFlash } = useCommonFlow();
  const { hideModal, showModal } = useSortationModal();
  const sortationClient = useSortationClient();

  useMount(() => {
    setNavbar({
      title: "Trailer Closeout Scan",
    });
  });

  useUnmount(() => {
    resetNotifications();
    setNavbar({});
  });

  const resetPage = useCallback(() => {
    resetNotifications();
    setBolId("");
    setTrailerId("");
    setSealNumber("");
    setTrailerCloseoutScanState(TrailerCloseoutScanState.AWAITING_BOL_SCAN);
    setNavbar({
      title: "Trailer Closeout Scan",
    });
  }, [setNavbar, resetNotifications]);

  const handleError = useCallback(
    (err) => {
      const errCode = err.message;
      emitFlash("DANGER");

      // Map error codes to appropriate messages
      let title = "Error";
      let text = "An error occurred";

      if (errCode === TrailerCloseoutModal.INVALID_BOL_ID) {
        title = "Invalid BOL ID";
        text = "The BOL ID format is invalid. Please scan a valid Shipwell BOL ID.";
      } else if (errCode === TrailerCloseoutModal.BOL_NOT_FOUND) {
        title = "BOL Not Found";
        text = "The BOL was not found in the system.";
      } else if (errCode === TrailerCloseoutModal.INVALID_BOL) {
        title = "Invalid BOL";
        text = "This BOL does not have a trailer assigned to it.";
      } else {
        title = "Error";
        text = `An unexpected error occurred: ${errCode}`;
      }

      showModal(errCode || TrailerCloseoutModal.UNKNOWN_ERROR, {
        title,
        text,
        hideModalValue: errCode || TrailerCloseoutModal.UNKNOWN_ERROR,
      });
    },
    [emitFlash, showModal]
  );

  const handleCloseout = useCallback(
    async (selectedSealNumber: string, selectedTrailerType: TrailerType) => {
      const ctx = logStart({ fn: "handleCloseout", bolId, trailerId, selectedSealNumber, selectedTrailerType });

      try {
        await sortationClient.closeOutBol(bolId, selectedSealNumber, selectedTrailerType);

        hideModal(TrailerCloseoutModal.TRAILER_TYPE_SELECTION);
        successResponse();
        addAutoCloseNotification(
          createSuccessNotification(`Trailer ${trailerId} has been closed out successfully.`),
          TOAST_AUTO_CLOSE
        );

        // Reset for next closeout
        resetPage();
      } catch (err) {
        logError(ctx, err);
        handleError(err);
      }
    },
    [bolId, trailerId, sortationClient, hideModal, successResponse, addAutoCloseNotification, resetPage, handleError]
  );

  const [onBolIdSubmitState, onBolIdSubmit] = useAsyncFn(
    async (input: string) => {
      const ctx = logStart({ fn: "onBolIdSubmit", input });

      if (!input.length) {
        return;
      }
      resetNotifications();

      if (!isValidShipwellBolId(input)) {
        setBolId("");
        handleError(new Error(TrailerCloseoutModal.INVALID_BOL_ID));
        return;
      }

      try {
        const existingBol = await sortationClient.getBillOfLading(input);
        if (!existingBol) {
          setBolId("");
          handleError(new Error(TrailerCloseoutModal.BOL_NOT_FOUND));
          return;
        }

        if (!existingBol.trailerId) {
          setBolId("");
          handleError(new Error(TrailerCloseoutModal.INVALID_BOL));
          return;
        }

        // Store BOL data before showing confirmation
        const currentBolId = input;
        const currentTrailerId = existingBol.trailerId;

        // Show trailer confirmation modal
        showModal(TrailerCloseoutModal.TRAILER_CONFIRMATION, {
          bolId: currentBolId,
          trailerId: currentTrailerId,
          onConfirm: () => {
            hideModal(TrailerCloseoutModal.TRAILER_CONFIRMATION);
            setBolId(currentBolId);
            setTrailerId(currentTrailerId);
            setSealNumber("");
            setTrailerCloseoutScanState(TrailerCloseoutScanState.AWAITING_SEAL_SCAN);
            infoResponse();
            addAutoCloseNotification(
              createSuccessNotification(`BOL ${currentBolId} confirmed. Please scan seal number.`),
              TOAST_AUTO_CLOSE
            );
            setNavbar({
              title: "Trailer Closeout Scan",
              rightAction: TextButton({ onClick: resetPage, defaultMessage: "Reset" }),
            });
          },
          onCancel: () => {
            hideModal(TrailerCloseoutModal.TRAILER_CONFIRMATION);
            setBolId("");
            setTrailerId("");
            setSealNumber("");
            setTrailerCloseoutScanState(TrailerCloseoutScanState.AWAITING_BOL_SCAN);
            setNavbar({
              title: "Trailer Closeout Scan",
            });
          },
        });
      } catch (err) {
        setBolId("");
        if (err.message === BillOfLadingError.BOL_NOT_FOUND) {
          handleError(new Error(TrailerCloseoutModal.BOL_NOT_FOUND));
          return;
        }
        logError(ctx, err);
        handleError(new Error(TrailerCloseoutModal.UNKNOWN_ERROR));
      }
    },
    [
      resetNotifications,
      handleError,
      sortationClient,
      hideModal,
      infoResponse,
      addAutoCloseNotification,
      resetPage,
      setNavbar,
      setBolId,
    ]
  );

  const [onSealNumberSubmitState, onSealNumberSubmit] = useAsyncFn(
    async (input: string) => {
      if (!input.length) {
        return;
      }
      resetNotifications();

      const sealValue = input;
      setSealNumber(sealValue);

      // Show trailer type selection modal immediately after seal scan
      showModal(TrailerCloseoutModal.TRAILER_TYPE_SELECTION, {
        bolId,
        trailerId,
        sealNumber: sealValue,
        onConfirm: async (trailerType: TrailerType) => {
          await handleCloseout(sealValue, trailerType);
        },
      });
    },
    [bolId, trailerId, resetNotifications, showModal, handleCloseout]
  );

  const scanConfig = useMemo(() => {
    switch (trailerCloseoutScanState) {
      case TrailerCloseoutScanState.AWAITING_BOL_SCAN:
        return {
          title: "Scan BOL QR Code",
          label: "BOL ID",
          value: bolId,
          onSubmit: onBolIdSubmit,
          onChange: setBolId,
          disabled: onBolIdSubmitState.loading,
        };
      case TrailerCloseoutScanState.AWAITING_SEAL_SCAN:
        return {
          title: "Scan Seal Number",
          label: "Seal Number",
          value: sealNumber,
          onSubmit: onSealNumberSubmit,
          onChange: setSealNumber,
          disabled: onSealNumberSubmitState.loading,
        };
    }
  }, [
    trailerCloseoutScanState,
    bolId,
    onBolIdSubmit,
    setBolId,
    onBolIdSubmitState,
    sealNumber,
    onSealNumberSubmit,
    setSealNumber,
    onSealNumberSubmitState,
  ]);

  return {
    bolId,
    trailerId,
    sealNumber,
    scanConfig,
    trailerCloseoutScanState,
  };
};
